import { format } from "date-fns";
import { it } from "date-fns/locale";

/**
 * `ipotesi_data` non è sempre una data: quando cade sul **giorno 1** è un
 * segnaposto per "quel mese", non un appuntamento preso. Chi pianifica sa che
 * l'installazione va fatta a settembre e scrive 01/09 perché il campo è una
 * data e vuole un giorno.
 *
 * Mostrarla come "1 set 2026" fa promettere all'app una precisione che il dato
 * non ha: si legge come una data concordata col cliente, e non lo è. Qui la si
 * rende come "Settembre 2026", che è l'informazione vera.
 *
 * Vale solo per `ipotesi_data`, mai per `data_effettiva`: lì il primo del mese
 * è un giorno in cui qualcuno è andato davvero sul posto.
 *
 * ## L'eccezione: `stato`
 *
 * La regola vale finché il giorno non è stato scelto attivamente. Trascinare
 * una tessera dalla pila "Da pianificare" su una cella del calendario (o
 * modificare a mano un record già preso in carico) NON è la stessa cosa che
 * scrivere 01/09 come promemoria di mese: è un gesto puntuale su un giorno
 * preciso, e quel gesto porta lo stato a "Programmata" (vedi
 * `pages/Schedule/Schedule.tsx`). Da lì in poi il giorno 1 è un giorno vero
 * come un altro, non più un segnaposto — altrimenti la tessera appena
 * trascinata sul 1° del mese sparirebbe di nuovo nella pila, e il gesto
 * risulterebbe senza effetto.
 *
 * "Rinviata" resta nel gruppo dei segnaposto: un'installazione rimandata a
 * settembre, senza un giorno preciso concordato, è esattamente lo stesso
 * promemoria di "Da programmare" — lo stato segnala solo che *prima* c'era
 * una data che è saltata, non che quella nuova sia stata negoziata.
 *
 * Il chiamante che non passa `stato` — o che passa `null` perché sul record
 * quel campo è vuoto — ottiene il comportamento di prima, solo sul giorno: è il
 * fallback sicuro quando non si può sapere se il record sia stato preso in
 * carico. Solo uno stato NOTO e diverso da quelli qui sotto disattiva la
 * regola, così una prop dimenticata in un punto nuovo non fa sparire in
 * silenzio l'etichetta di mese.
 *
 * Costo accettato: un'installazione ancora "Da programmare" o "Rinviata",
 * ipotizzata *davvero* per il 1° del mese, si legge come "tutto il mese". È
 * indistinguibile nei dati finché non viene programmata — i due casi scrivono
 * lo stesso valore — e il segnaposto è di gran lunga il più frequente in
 * quegli stati.
 */

/** Stati in cui il giorno 1 di `ipotesi_data` è ancora un segnaposto di mese,
 * non un giorno scelto. Da "Programmata" in poi (In corso, Effettuata) il
 * giorno è stato fissato attivamente e va letto alla lettera. */
const STATI_SEGNAPOSTO = new Set(["Da programmare", "Rinviata"]);

/** La data ISO senza fuso: `ipotesi_data` è una colonna DATE, l'ora non esiste. */
function parteData(valore: string | Date): string {
  return typeof valore === "string" ? valore.slice(0, 10) : format(valore, "yyyy-MM-dd");
}

/**
 * true se la data è un segnaposto di mese (giorno 1).
 *
 * `stato`, se noto, restringe la regola agli stati non ancora programmati
 * (vedi sopra): un record "Programmata"/"In corso"/"Effettuata" con giorno 1
 * NON è più un segnaposto. Omettendolo — o passando `null` — si ottiene il
 * vecchio comportamento basato solo sul giorno.
 */
export function isMesePlaceholder(
  valore: string | Date | null | undefined,
  stato?: string | null
): boolean {
  if (!valore) return false;
  // `!= null` e non `!== undefined`: copre anche il `null` esplicito, che vuol
  // dire "il record non ha uno stato", non "lo stato non è fra questi".
  if (stato != null && !STATI_SEGNAPOSTO.has(stato)) return false;
  return parteData(valore).slice(8, 10) === "01";
}

/**
 * Etichetta da mostrare per una data ipotizzata: "Settembre 2026" se è un
 * segnaposto di mese, altrimenti la data secca "22 set 2026".
 *
 * `stato` ha lo stesso ruolo che in `isMesePlaceholder`: su un record già
 * programmato il 1° del mese si mostra come giorno vero.
 *
 * Il fallback per il valore assente lo decide il chiamante — nella lista è
 * "Senza data", in un popup può essere una riga che non compare affatto.
 */
export function etichettaIpotesiData(
  valore: string | Date | null | undefined,
  stato?: string | null
): string | null {
  if (!valore) return null;
  // Si costruisce la Date dalle sole parti della data, così il fuso non può
  // spostare il giorno: `new Date("2026-09-01T00:00:00Z")` a ovest di Greenwich
  // diventerebbe il 31 agosto, e il segnaposto sparirebbe.
  const [anno, mese, giorno] = parteData(valore).split("-").map(Number);
  const d = new Date(anno, mese - 1, giorno);
  if (Number.isNaN(d.getTime())) return null;
  return isMesePlaceholder(valore, stato)
    ? format(d, "LLLL yyyy", { locale: it }).replace(/^./, (c) => c.toUpperCase())
    : format(d, "d MMM yyyy", { locale: it });
}

/**
 * true se la data è nel futuro rispetto a oggi.
 *
 * Il confronto è fra stringhe `yyyy-MM-dd`, non fra oggetti Date: le colonne
 * sono `@db.Date` e arrivano a mezzanotte UTC, quindi un `new Date(iso) >
 * new Date()` risponderebbe in modo diverso a seconda del fuso di chi guarda —
 * ed è lo stesso motivo per cui `etichettaIpotesiData` costruisce la data dalle
 * parti. Sulle stringhe l'ordinamento lessicale coincide con quello cronologico
 * e non c'è nessun fuso di mezzo.
 *
 * Oggi NON è futuro: un sopralluogo previsto per stamattina non è più una
 * previsione, è il presente.
 */
export function isFutura(valore: string | Date | null | undefined): boolean {
  if (!valore) return false;
  return parteData(valore) > format(new Date(), "yyyy-MM-dd");
}
